import React, { memo, type ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  FooterActionBar,
  Icon,
  SidePanel,
} from '@saviynt/design-system';
import cx from 'classnames';

import { VIOLATIONS_DETAILS_TABS } from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { detailsActions } from '../../../data-store/detailsSlice';
import {
  getHeaderDescription,
  getHeaderIcon,
  getHeaderTitle,
} from '../../../utils';

import ViolationsTabDetailsConflictSP from './ViolationsTabDetailsConflictSP';
import ViolationsTabDetailsMitigateSP from './ViolationsTabDetailsMitigateSP';

import './ViolationsTabDetailsSidePanel.css';

interface ViolationsTabDetailsSidePanelProps {
  activeTab: string;
}

/**
 * ViolationsTabDetailsSidePanel
 */
function ViolationsTabDetailsSidePanel({
  activeTab,
}: ViolationsTabDetailsSidePanelProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    sidepanel: { isSidePanelVisible },
    violationsTab: {
      conflicts: { selectedRowData: conflictsRowData },
      mitigate: { selectedRowData: mitigateRowData },
    },
  } = useTypedSelector((state) => state.aag.details);

  const isConflictsTab = Boolean(
    conflictsRowData && activeTab === VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS
  );
  const isMitigatingTab = Boolean(
    mitigateRowData && activeTab === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING
  );

  const handleSidePanelClose = () =>
    dispatch(detailsActions.setSidePanelClose());

  const header = useMemo(() => {
    if (isConflictsTab) {
      return {
        title: getHeaderTitle(
          conflictsRowData as {
            name?: string;
            entitlementDisplayName?: string;
          },
          conflictsRowData?.tracker.level
        ),
        description: l(getHeaderDescription(conflictsRowData?.tracker.level)),
        icon: getHeaderIcon(conflictsRowData?.tracker.level),
      };
    }

    if (isMitigatingTab) {
      return {
        title: mitigateRowData?.name,
        description: l('Mitigating Control'),
        icon: 'SodMitigate',
      };
    }

    return {};
  }, [l, isConflictsTab, isMitigatingTab, conflictsRowData, mitigateRowData]);

  const getLayout = useCallback(() => {
    if (isConflictsTab) {
      return <ViolationsTabDetailsConflictSP />;
    }

    if (isMitigatingTab) {
      return <ViolationsTabDetailsMitigateSP />;
    }

    return undefined;
  }, [isConflictsTab, isMitigatingTab]);

  const getFooter = useCallback(() => {
    if (isMitigatingTab) {
      return (
        <FooterActionBar className='Sod-ViolationsTabDetailsSidePanel-footerActionBar'>
          <Button
            isDisabled
            kind='outlined'
            size='medium'
            onClick={() => {}}
            leftIcon={<Icon kind='ExtendEndDate' />}>
            {l('Change End Date')}
          </Button>
          <Button
            isDisabled
            kind='outlined'
            size='medium'
            onClick={() => {}}
            leftIcon={<Icon kind='Delete' />}>
            {l('Remove')}
          </Button>
        </FooterActionBar>
      );
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should not rerender for localize function
  }, [isMitigatingTab]);

  return (
    <SidePanel
      isOpen={isSidePanelVisible}
      onClose={handleSidePanelClose}
      appIconSize='medium'
      header={header}
      layout={getLayout()}
      footer={getFooter()}
      className='Sod-ViolationsTabDetailsSidePanel'
      layoutClassName='Sod-ViolationsTabDetailsSidePanel-sidePanelLayout'
      headerClassName='Sod-ViolationsTabDetailsSidePanel-sidePanelHeader'
      innerContentClassName={cx(
        'Sod-ViolationsTabDetailsSidePanel-sidePanelInnerContent',
        isMitigatingTab && 'Sod-ViolationsTabDetailsMitigateSP--isMitigatingTab'
      )}
      footerClassName={cx(
        'Sod-ViolationsTabDetailsSidePanel-footer',
        !isMitigatingTab &&
          'Sod-ViolationsTabDetailsSidePanel-footer--isConflictTab'
      )}
    />
  );
}

export default memo(ViolationsTabDetailsSidePanel);
